import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Clock, Globe, Eye, EyeOff, LogIn } from 'lucide-react';

export default function LoginPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success(t('success'));
      navigate('/admin/dashboard');
    } else {
      toast.error(result.error || t('invalidCredentials'));
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex font-body" data-testid="login-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1771970472779-6452fbce94d6?crop=entropy&cs=srgb&fm=jpg&q=85)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-punchtm.png" alt="PunchTM" className="h-12 w-auto" />
          </div>
          
          <div>
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              {language === 'fr' 
                ? 'Pointage industriel simple et rapide'
                : 'Simple and fast industrial time tracking'}
            </h2>
            <p className="text-white/60 text-lg">
              {language === 'fr'
                ? 'Gérez la présence de vos employés en temps réel avec photo obligatoire.'
                : 'Manage your employee attendance in real-time with mandatory photo.'}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-success animate-pulse" />
            </div>
            <div>
              <p className="text-white font-medium">
                {language === 'fr' ? 'Moins de 3 secondes' : 'Under 3 seconds'}
              </p>
              <p className="text-white/60 text-sm">
                {language === 'fr' ? 'par pointage' : 'per punch'}
              </p>
            </div>
          </div>
          
          <p className="text-white/40 text-xs" data-testid="powered-by-tm">
            {language === 'fr' 
              ? 'Propulsé par TM Consulting et Formation'
              : 'Powered by TM Consulting et Formation'}
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/logo-punchtm.png" alt="PunchTM" className="h-14 w-auto" />
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-2xl">{t('login')}</CardTitle>
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-600"
                  data-testid="login-language-toggle"
                >
                  <Globe size={16} />
                  <span className="text-xs font-bold uppercase">{language}</span>
                </button>
              </div>
              <CardDescription>
                {language === 'fr' 
                  ? 'Connectez-vous pour accéder au tableau de bord'
                  : 'Sign in to access the dashboard'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 font-bold"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <LogIn className="mr-2" size={20} />
                      {t('signIn')}
                    </>
                  )}
                </Button>
              </form>

              {/* Demo credentials hint */}
              <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                <p className="text-xs text-slate-500 font-medium mb-2">
                  {language === 'fr' ? 'Identifiants demo :' : 'Demo credentials:'}
                </p>
                <p className="text-sm text-slate-700 font-mono">nathalie.cote@antarogeomatik.com</p>
                <p className="text-sm text-slate-700 font-mono">Antgeo2024!</p>
              </div>
            </CardContent>
          </Card>

          {/* Kiosk link */}
          <div className="mt-6 text-center">
            <a 
              href="/kiosk" 
              className="text-sm text-slate-500 hover:text-primary transition-colors"
              data-testid="goto-kiosk-link"
            >
              {language === 'fr' ? '→ Accéder au mode kiosque' : '→ Go to kiosk mode'}
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
